import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/lib/api-client";
import { usePollStatus } from "@/hooks/useZoteroSource";
import type { ZoteroSyncEvent } from "@/types/zoteroSync";

const QK_RECENT = ["zotero-sync-events", "matched"];
const QK_UNMATCHED = ["zotero-sync-events", "unmatched"];
// Terminal "we gave up" events. Surface in the same tray as unmatched
// (the user has the same single-decision flow: Retry vs. live with it),
// but query separately so the unmatched query keeps its strict filter.
const QK_STUCK = ["zotero-sync-events", "skipped_blob_unreachable"];

// Event ids that the user has optimistically removed from the unmatched
// list (currently: events being added-to-concept) but whose server-side
// commit hasn't landed yet. The add-to-concept endpoint takes ~10-15s
// (R2 + PDF text extraction); during that window a concurrent refetch
// (e.g. from refetchOnMount when the user reopens the dialog) returns
// the row STILL with status="unmatched" and overwrites the optimistic
// filter — making the row "flash back" until the mutation completes.
// Filtering inside the queryFn guarantees the row never re-enters the
// cache regardless of when the refetch lands. Cleared on settle.
const pendingAddRemovalIds = new Set<string>();

export function useZoteroSyncEvents() {
  const qc = useQueryClient();
  usePollStatus(); // Watches focused-poll lifecycle; on completion,
                   // invalidates the QK_UNMATCHED + QK_RECENT queries
                   // so events surface ~2s after the BackgroundTask ends.
  const recent = useQuery({
    queryKey: QK_RECENT,
    queryFn: () =>
      apiClient
        .get("api/zotero-sync/events?status=matched&limit=10")
        .json<ZoteroSyncEvent[]>(),
    refetchInterval: 30_000,
  });
  const unmatched = useQuery({
    queryKey: QK_UNMATCHED,
    queryFn: async () => {
      const r = await apiClient
        .get("api/zotero-sync/events?status=unmatched&limit=50")
        .json<ZoteroSyncEvent[]>();
      // Filter out rows the user has optimistically removed but whose
      // server commit is still in flight. Prevents the row from
      // "flashing back" during the ~13s add-to-concept mutation window
      // if the user closes + reopens the dialog (refetchOnMount races
      // the mutation's onSuccess).
      return r.filter((e) => !pendingAddRemovalIds.has(e.id));
    },
    refetchInterval: 30_000,
  });
  const stuck = useQuery({
    queryKey: QK_STUCK,
    queryFn: () =>
      apiClient
        .get("api/zotero-sync/events?status=skipped_blob_unreachable&limit=50")
        .json<ZoteroSyncEvent[]>(),
    refetchInterval: 30_000,
  });

  // When the poller surfaces a new matched event since last seen, the
  // attached PDF has just landed on the paper — canonical_pdf_status
  // is stale in any open paper table. Invalidate broadly; affected
  // queries refetch on next focus.
  const seenIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const list = recent.data;
    if (!list) return;
    const ids = new Set(list.map((e) => e.id));
    if (seenIdsRef.current == null) {
      seenIdsRef.current = ids;
      return;
    }
    let hasNew = false;
    for (const id of ids) {
      if (!seenIdsRef.current.has(id)) {
        hasNew = true;
        break;
      }
    }
    seenIdsRef.current = ids;
    if (hasNew) {
      qc.invalidateQueries({ queryKey: ["papers"] });
      qc.invalidateQueries({ queryKey: ["paper-keys"] });
      qc.invalidateQueries({ queryKey: ["concept-papers"] });
      qc.invalidateQueries({ queryKey: ["concept-paper"] });
    }
  }, [recent.data, qc]);

  const unreadCount = (recent.data ?? []).filter((e) => !e.viewed_at).length;
  const unmatchedList = unmatched.data ?? [];
  const stuckList = stuck.data ?? [];
  // Merge stuck blob events into the unmatched list so the tray renders
  // both row types together. Bell counter sums both (see unmatchedCount).
  const unmatchedMerged = [...unmatchedList, ...stuckList].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const unmatchedCount = unmatchedList.length + stuckList.length;
  return {
    recent: recent.data ?? [],
    unmatched: unmatchedMerged,
    unreadCount,
    unmatchedCount,
    isLoading: recent.isLoading,
  };
}

export function useMarkEventsViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post("api/zotero-sync/events/view", { json: ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_RECENT });
    },
  });
}

export function useDismissEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`api/zotero-sync/events/${id}/dismiss`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_UNMATCHED });
    },
  });
}

export function useMatchEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paper_id }: { id: string; paper_id: string }) =>
      apiClient.post(`api/zotero-sync/events/${id}/match`, {
        json: { paper_id },
        // Endpoint does R2 download + sha256 + R2 upload + PDF text
        // extraction; default 10s ky timeout is way too tight (CLAUDE.md #9).
        timeout: 120_000,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_UNMATCHED });
      qc.invalidateQueries({ queryKey: QK_RECENT });
      qc.invalidateQueries({ queryKey: ["papers"] });
    },
  });
}

export function useReplaceEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`api/zotero-sync/events/${id}/replace`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_RECENT });
      qc.invalidateQueries({ queryKey: ["papers"] });
    },
  });
}

export function useAddEventToConcept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      concept_id,
    }: {
      id: string;
      concept_id: string;
    }) =>
      apiClient.post(`api/zotero-sync/events/${id}/add-to-concept`, {
        json: { concept_id },
        // Endpoint does add_papers_to_concept + R2 download + sha256 +
        // R2 upload + PDF text extraction; default 10s ky timeout is
        // way too tight (CLAUDE.md #9). The diagnostic that fixed the
        // counter-bounce bug: 10s timeout fired → onError rolled back
        // optimistic filter → bell pill bounced 0→1→0.
        timeout: 120_000,
      }),
    onMutate: async ({ id }) => {
      // Track the id so any in-flight or future refetch can filter it
      // out (see queryFn filter above) — survives dialog unmount/remount
      // during the mutation's long server-side processing window.
      pendingAddRemovalIds.add(id);
      await qc.cancelQueries({ queryKey: QK_UNMATCHED });
      const previous = qc.getQueryData<ZoteroSyncEvent[]>(QK_UNMATCHED);
      if (previous) {
        qc.setQueryData<ZoteroSyncEvent[]>(
          QK_UNMATCHED,
          previous.filter((e) => e.id !== id),
        );
      }
      return { previous };
    },
    onSuccess: async (_data, { id }) => {
      await qc.cancelQueries({ queryKey: QK_UNMATCHED });
      qc.setQueryData<ZoteroSyncEvent[]>(QK_UNMATCHED, (prev) =>
        prev ? prev.filter((e) => e.id !== id) : prev,
      );
    },
    onError: (_err, { id }, ctx) => {
      // Restore — the optimistic removal didn't take.
      pendingAddRemovalIds.delete(id);
      if (ctx?.previous) {
        qc.setQueryData(QK_UNMATCHED, ctx.previous);
      }
    },
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: QK_UNMATCHED });
      qc.invalidateQueries({ queryKey: QK_RECENT });
      qc.invalidateQueries({ queryKey: ["papers"] });
      qc.invalidateQueries({ queryKey: ["paper-keys"] });
      qc.invalidateQueries({ queryKey: ["concept-papers"] });
      // Clear after the invalidate-triggered refetch has had time to
      // land with the post-mutation server state. The 60s window is
      // very conservative; once the server reflects the new status,
      // leaving the id in the set is harmless (it just won't match
      // anything in incoming responses).
      setTimeout(() => pendingAddRemovalIds.delete(id), 60_000);
    },
  });
}
